import { Input } from '@/components/atoms'
import { cn } from '@/utils/cn'
import type { InstitutionalProfileFormData } from '@/types/institutionalProfile'
import { CheckboxRow, SectionHeading } from './FormField'
import {
  COMPLIANCE_COPY,
  COMPLIANCE_FIELDS,
  QUALIFICATION_COPY,
  QUALIFICATION_FIELDS,
} from './institutionalFields'
import { BODY_TEXT, ERROR_TEXT, FIELD_LABEL, HELP_TEXT } from './styles'

/** The attestation-row fields, which live on the attestation and not the profile. */
export interface EntityAttestationValues {
  country: string
  affirmation: boolean
  subscribe_to_newsletter: boolean
}

interface EntityAttestationStepProps {
  profile: InstitutionalProfileFormData
  attestation: EntityAttestationValues
  onProfileChange: (name: keyof InstitutionalProfileFormData, value: string | boolean) => void
  onAttestationChange: (updates: Partial<EntityAttestationValues>) => void
  errors: Record<string, string>
  signerEmail: string
  disabled?: boolean
}

/**
 * Step 2 — the entity attestation itself.
 *
 * Two groups with different rules, and the server enforces both on submit:
 *
 *  - **Qualification** — at least ONE of the three must be true. A 400 reports
 *    this as a single composite entry
 *    (`"accredited_investor_status or qualified_..."`) because the server cannot
 *    know which one the entity meant; the error therefore attaches to the group,
 *    under the synthetic key `__qualification`.
 *  - **Compliance** — ALL FOUR must be true, and each is reported by its own name.
 *
 * `country` and `affirmation` sit on the attestation row rather than the
 * profile, which is why they are threaded separately.
 */
export const EntityAttestationStep = ({
  profile,
  attestation,
  onProfileChange,
  onAttestationChange,
  errors,
  signerEmail,
  disabled,
}: EntityAttestationStepProps) => (
  <div className="space-y-10">
    <section>
      <SectionHeading
        title="Entity qualification"
        description="Select every category the entity meets. At least one is required."
      />
      {errors.__qualification && (
        <p className={cn(ERROR_TEXT, 'mb-2')}>{errors.__qualification}</p>
      )}
      <div className="space-y-3">
        {QUALIFICATION_FIELDS.map(name => (
          <CheckboxRow
            key={name}
            id={name}
            checked={Boolean(profile[name])}
            onChange={checked => onProfileChange(name, checked)}
            label={QUALIFICATION_COPY[name].label}
            help={QUALIFICATION_COPY[name].help}
            error={errors[name]}
            disabled={disabled}
          />
        ))}
      </div>

      <div className="mt-4">
        <label className={FIELD_LABEL} htmlFor="other_certifications">
          Other certifications
        </label>
        <Input
          id="other_certifications"
          value={profile.other_certifications}
          onChange={event => onProfileChange('other_certifications', event.target.value)}
          placeholder="Anything else worth recording (optional)"
          disabled={disabled}
          className="h-[52px] font-plus-jakarta text-[14px]"
        />
      </div>
    </section>

    <section>
      <SectionHeading
        title="Compliance attestations"
        description="All four are required for an institutional account."
      />
      <div className="space-y-3">
        {COMPLIANCE_FIELDS.map(name => (
          <CheckboxRow
            key={name}
            id={name}
            checked={Boolean(profile[name])}
            onChange={checked => onProfileChange(name, checked)}
            label={COMPLIANCE_COPY[name].label}
            help={COMPLIANCE_COPY[name].help}
            error={errors[name]}
            disabled={disabled}
          />
        ))}
      </div>
    </section>

    <section>
      <SectionHeading title="Signing details" />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label className={FIELD_LABEL} htmlFor="attestation_country">
            Country of incorporation
          </label>
          <Input
            id="attestation_country"
            value={attestation.country}
            onChange={event => onAttestationChange({ country: event.target.value })}
            placeholder="United States"
            disabled={disabled}
            className={cn('h-[52px] font-plus-jakarta text-[14px]', errors.country && 'border-[#ff540c]')}
          />
          {errors.country && <p className={ERROR_TEXT}>{errors.country}</p>}
        </div>

        <div>
          <label className={FIELD_LABEL} htmlFor="attestation_signer">
            Signing as
          </label>
          <Input
            id="attestation_signer"
            value={signerEmail}
            readOnly
            disabled
            className="h-[52px] font-plus-jakarta text-[14px]"
          />
          <p className={cn(HELP_TEXT, 'mt-1')}>
            The attestation is recorded against your account.
          </p>
        </div>
      </div>

      <div className="mt-5 space-y-3">
        <CheckboxRow
          id="affirmation"
          checked={attestation.affirmation}
          onChange={checked => onAttestationChange({ affirmation: checked })}
          label="I affirm, on behalf of the entity, that the above is true and complete"
          help="You confirm you are authorised to make this attestation for the entity, and that SecondLane may rely on it."
          error={errors.affirmation}
          disabled={disabled}
        />
        <CheckboxRow
          id="subscribe_to_newsletter"
          checked={attestation.subscribe_to_newsletter}
          onChange={checked => onAttestationChange({ subscribe_to_newsletter: checked })}
          label="Send me monthly market insights"
          help="Optional. You can change this at any time."
          disabled={disabled}
        />
      </div>

      <p className={cn(BODY_TEXT, 'mt-4')}>
        Everyone you invite to this organization inherits its accreditation — they do not attest
        individually.
      </p>
    </section>
  </div>
)
